/**
 * Utility functions for handling photo URLs in the application
 */

/**
 * Generates the proper URL for displaying a photo
 * @param photo - The photo object containing url, key, and _id
 * @param agencySlug - The agency slug for the file serving endpoint
 * @returns The proper URL for displaying the photo
 */
export const getPhotoUrl = (photo: any, agencySlug?: string): string => {
  // If the photo has a key, use the proper file serving endpoint
  if (photo.key) {
    // Use the provided agency slug or fallback to 'default'
    const slug = agencySlug || 'default';
    return `/api/agencies/${slug}/files/${photo._id}`;
  }
  
  // Fallback to direct URL if no key
  return photo.url;
};

/**
 * Generates the proper URL for downloading a photo
 * @param photo - The photo object containing url, key, and _id
 * @param agencySlug - The agency slug for the file serving endpoint
 * @returns The proper URL for downloading the photo
 */
export const getPhotoDownloadUrl = (photo: any, agencySlug?: string): string => {
  // If the photo has a key, use the proper file serving endpoint
  if (photo.key) {
    // Use the provided agency slug or fallback to 'default'
    const slug = agencySlug || 'default';
    return `/api/agencies/${slug}/files/${photo._id}`;
  }
  
  // Fallback to direct URL if no key
  return photo.url;
};

/**
 * Checks if a photo URL is a direct MinIO URL that needs to be converted
 * @param url - The photo URL to check
 * @returns True if the URL is a direct MinIO URL
 */
export const isDirectMinIOUrl = (url: string): boolean => {
  return url.includes('localhost:9000') || url.includes('minio');
};

/**
 * Converts a direct MinIO URL to a proper file serving URL
 * @param url - The direct MinIO URL
 * @param photoId - The photo ID
 * @param agencySlug - The agency slug
 * @returns The proper file serving URL
 */
export const convertMinIOUrl = (url: string, photoId: string, agencySlug?: string): string => {
  const slug = agencySlug || 'default';
  return `/api/agencies/${slug}/files/${photoId}`;
};

/**
 * Generates the proper URL for displaying a contractor job photo (public access)
 * @param photo - The photo object containing url, key, and _id
 * @param agencySlug - The agency slug for the file serving endpoint
 * @returns The proper URL for displaying the contractor job photo
 */
export const getContractorJobPhotoUrl = (photo: any, agencySlug?: string): string => {
  // If the photo has a key, use the public job files endpoint
  if (photo.key) {
    const slug = agencySlug || 'default';
    return `/api/agencies/${slug}/job-files/${photo._id}`;
  }
  
  // Fallback to direct URL if no key
  return photo.url;
};